/**
 * Hand-typed Report QA API client.
 *
 * Mirrors the contract locked in `backend/schemas/report_generator.py`
 * (`AIUtilsReportQAResponseSchema` / `ReportQAResultSchema`).
 *
 * Lives outside `api/generated/` because the OpenAPI spec hasn't been
 * regenerated to include `/report-qa` and `/report-qa/latest` yet —
 * delete this file and switch the hooks over to the generated SDK
 * once `scripts/update-api-client.sh` picks them up.
 */
import { apiClient } from "./apiClient";

// Local, not the shared `buildApiErrorMessage` (utils/notification): the
// api/ layer only imports from types/, and this hand-typed client is slated
// for deletion once these endpoints land in api/generated/ (see file header).
const buildErrorMessage = (prefix: string, error: unknown): string => {
  const detail =
    error && typeof error === "object" && "detail" in error
      ? (error as { detail?: unknown }).detail
      : undefined;
  return `${prefix}: ${typeof detail === "string" ? detail : JSON.stringify(error)}`;
};

export type ReportQASectionStatus = "strong" | "weak" | "missing";

export type ReportQASuggestion = {
  text: string;
  learn_more_url?: string | null;
};

export type ReportQASectionEvaluation = {
  name: string;
  status: ReportQASectionStatus;
  note?: string | null;
  suggestions: ReportQASuggestion[];
};

export type ReportQAResult = {
  id: string;
  response_id: string;
  run_by_user_id: string;
  source_docx_url: string;
  summary?: string | null;
  overall_score?: number | null;
  severity_overall?: string | null;
  section_evaluations_json?: ReportQASectionEvaluation[] | null;
  raw_markdown?: string | null;
  prompt_tokens?: number | null;
  completion_tokens?: number | null;
  created_at: string;
};

type ReportQAArgs = {
  templateId: string;
  caseNumber: string;
};

const buildUrl = ({ templateId, caseNumber }: ReportQAArgs, suffix: string) =>
  `/api/v1/report-generator/templates/${encodeURIComponent(
    templateId
  )}/cases/${encodeURIComponent(caseNumber)}/report-qa${suffix}`;

// @hey-api/client-fetch's `MethodFn` unwraps `TData` via
// `TData[keyof TData]` when TData is a Record. The generated SDK
// satisfies that by passing the status-code → response map directly
// (e.g. `{ 200: ReportQAResult }`); we mirror that here so the
// typing flows the same way once these endpoints land in
// `api/generated/`.
type LatestResponseMap = { 200: ReportQAResult | null };
type RunResponseMap = { 200: ReportQAResult };

export const getLatestReportQA = async (
  args: ReportQAArgs
): Promise<ReportQAResult | null> => {
  const { data, error } = await apiClient.get<LatestResponseMap>({
    url: buildUrl(args, "/latest"),
  });
  if (error) {
    throw new Error(buildErrorMessage("Failed to load latest Report QA", error));
  }
  return data ?? null;
};

export const runReportQA = async (args: ReportQAArgs): Promise<ReportQAResult> => {
  const { data, error } = await apiClient.post<RunResponseMap>({
    url: buildUrl(args, ""),
  });
  if (error) {
    throw new Error(buildErrorMessage("Failed to run Report QA", error));
  }
  if (!data) {
    throw new Error("Report QA: empty response from server");
  }
  return data;
};
